import { apiClient } from '../lib/api/client';
import type { ProfileData, UserProfile, WorkExperience } from '../models/v2/profile';

export const profileService = {
    /**
     * Get full profile overview data
     * GET /api/profile
     */
    getProfile: async (): Promise<ProfileData> => {
        try {
            const response = await apiClient.get<ProfileData>('/api/profile');
            return response;
        } catch (error) {
            console.warn("API '/api/profile' failed, using mock data for UI preview.", error);

            // Mock Data for UI Verification
            return {
                profile: {
                    id: 'mock-1',
                    preferred_name: 'Michael Okpechi',
                    headline: 'Mobile Application Developer @ Stroll',
                    location: 'Coventry, UK',
                    actively_looking: true
                },
                resumes: [
                    { id: '1', name: 'Chibuikem_Okpechi_resume.pdf', file_url: '#', created_at: new Date().toISOString(), is_default: true }
                ],
                work_experiences: [
                    {
                        id: '1',
                        position: 'Mobile Application Developer',
                        company: 'Stroll',
                        location: 'Remote',
                        start_date: '2025-01-01',
                        is_current: true,
                        description: 'Integrated Daily-Pool feature, increasing match probability by 10%.\nBuilt and optimized photo upload system.'
                    },
                    {
                        id: '2',
                        position: 'Mobile Application Developer',
                        company: 'PressOne Africa',
                        location: 'Lagos, Nigeria',
                        start_date: '2024-10-01',
                        end_date: '2025-02-01',
                        is_current: false,
                        description: 'Designed and launched feedback system in Swift iOS app.'
                    }
                ],
                educations: [
                    {
                        id: '1',
                        institution: 'Coventry University',
                        degree: 'Master\'s',
                        field_of_study: 'Computer Science',
                        start_date: '2025-09-01',
                        end_date: '2026-09-01',
                        grade: 'Distinction'
                    }
                ],
                skills: [
                    { id: '1', name: 'Flutter', is_highlighted: true },
                    { id: '2', name: 'iOS/Swift', is_highlighted: true },
                    { id: '3', name: 'Git', is_highlighted: false },
                    { id: '4', name: 'React', is_highlighted: false }
                ],
                links: [
                    { id: '1', platform: 'linkedin', url: 'https://linkedin.com/in/michael', label: 'LinkedIn' },
                    { id: '2', platform: 'github', url: 'https://github.com/mik3yyy', label: 'GitHub' }
                ]
            };
        }
    },

    /**
     * Update personal info
     * PATCH /api/profile/personal-info
     */
    updatePersonalInfo: async (data: Partial<UserProfile>): Promise<{ profile: UserProfile }> => {
        const response = await apiClient.patch<{ profile: UserProfile }>('/api/profile/personal-info', data);
        return response;
    },

    /**
     * Add Work Experience
     * POST /api/profile/work-experience
     */
    addWorkExperience: async (data: Omit<WorkExperience, 'id'>): Promise<{ experience: WorkExperience }> => {
        const response = await apiClient.post<{ experience: WorkExperience }>('/api/profile/work-experiences', data);
        return response;
    },

    /**
     * Update Work Experience
     * PUT /api/profile/work-experience/:id
     */
    updateWorkExperience: async (id: string, data: Partial<WorkExperience>): Promise<{ experience: WorkExperience }> => {
        const response = await apiClient.put<{ experience: WorkExperience }>(`/api/profile/work-experiences/${id}`, data);
        return response;
    },

    /**
    * Delete Work Experience
    * DELETE /api/profile/work-experiences/:id
    */
    deleteWorkExperience: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/profile/work-experiences/${id}`);
    },

    /**
     * Add Education
     * POST /api/profile/educations
     */
    addEducation: async (data: Omit<import('../models/v2/profile').Education, 'id'>): Promise<{ education: import('../models/v2/profile').Education }> => {
        const response = await apiClient.post<{ education: import('../models/v2/profile').Education }>('/api/profile/educations', data);
        return response;
    },

    /**
     * Update Education
     * PUT /api/profile/educations/:id
     */
    updateEducation: async (id: string, data: Partial<import('../models/v2/profile').Education>): Promise<{ education: import('../models/v2/profile').Education }> => {
        const response = await apiClient.put<{ education: import('../models/v2/profile').Education }>(`/api/profile/educations/${id}`, data);
        return response;
    },

    /**
     * Delete Education
     * DELETE /api/profile/educations/:id
     */
    deleteEducation: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/profile/educations/${id}`);
    },

    /**
     * Get Available Skills (for search)
     * GET /api/skills
     */
    getAvailableSkills: async (): Promise<import('../models/v2/profile').Skill[]> => {
        try {
            return await apiClient.get<import('../models/v2/profile').Skill[]>('/api/skills');
        } catch (error) {
            console.warn("API '/api/skills' failed, using mock data.", error);
            // Mock Skills
            return [
                { id: 's1', name: 'React', is_highlighted: false },
                { id: 's2', name: 'TypeScript', is_highlighted: false },
                { id: 's3', name: 'Python', is_highlighted: false },
                { id: 's4', name: 'Node.js', is_highlighted: false },
                { id: 's5', name: 'Flutter', is_highlighted: false },
                { id: 's6', name: 'Swift', is_highlighted: false },
                { id: 's7', name: 'Kotlin', is_highlighted: false },
                { id: 's8', name: 'AWS', is_highlighted: false },
                { id: 's9', name: 'Docker', is_highlighted: false },
                { id: 's10', name: 'GraphQL', is_highlighted: false },
                { id: 's11', name: 'PostgreSQL', is_highlighted: false },
                { id: 's12', name: 'System Design', is_highlighted: false }
            ];
        }
    },

    /**
     * Update User Skills (Atomic Replace)
     * PUT /api/profile/skills
     */
    updateSkills: async (skills: import('../models/v2/profile').Skill[]): Promise<{ skills: import('../models/v2/profile').Skill[] }> => {
        const response = await apiClient.put<{ skills: import('../models/v2/profile').Skill[] }>('/api/profile/skills', skills);
        return response;
    },

    /**
     * Update User Links (Atomic Replace)
     * PUT /api/profile/links
     */
    updateLinks: async (links: import('../models/v2/profile').Link[]): Promise<{ links: import('../models/v2/profile').Link[] }> => {
        const response = await apiClient.put<{ links: import('../models/v2/profile').Link[] }>('/api/profile/links', links);
        return response;
    },

    /**
     * Populate profile from resume using AI
     * POST /api/profile/populate_from_resume
     */
    populateFromResume: async (replaceCurrentData: boolean = false, resumeId?: string): Promise<{ message: string }> => {
        const payload = {
            replace_current_data: replaceCurrentData,
            resume_id: resumeId
        };
        const response = await apiClient.post<{ message: string }>('/api/profile/populate_from_resume', payload);
        return response;
    }
};
